import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

export default function AdminDangerZone() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const preview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/purge-demo-data", { confirm: "DRYRUN", dry_run: true });
      setLastResult(data);
      toast.message(`Preview — ${data.total_matched} records would be removed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const purge = async () => {
    if (confirmText !== "PURGE") {
      toast.error('Type "PURGE" exactly to confirm');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge-demo-data", { confirm: "PURGE" });
      setLastResult(data);
      toast.success(`Removed ${data.total_removed} test records`);
      setOpen(false);
      setConfirmText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-12" data-testid="admin-danger-zone">
      <div className="mb-4">
        <div className="vq-overline mb-2 text-rose-700 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Danger zone
        </div>
        <h2 className="font-display font-bold text-2xl text-slate-900">Clean workspace</h2>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Purge test/demo records left by testing agents and pytest seeds. Removes CAPAs,
          deviations, changes, complaints, risks, documents, OCR docs, training assignments,
          management reviews, cyber threats, traces and test devices matching known test
          markers <span className="font-mono text-xs">(Test, pytest, seed, Regulatory response)</span> or
          created by <span className="font-mono text-xs">demo@…</span>. Users, payments, promo
          codes, audit events, e-signatures and leads are preserved.
        </p>
      </div>

      <div className="vq-card p-5 border-rose-200 bg-rose-50/30">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-bold text-slate-900">Purge demo/test data</div>
            <div className="text-xs text-slate-600 mt-1">
              Preview first to see what would be removed, then commit. Irreversible once run.
              An audit event is written recording the actor and per-collection counts.
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline"
                    onClick={preview}
                    disabled={previewing}
                    data-testid="admin-purge-preview-btn"
                    className="border-slate-300 text-slate-700 font-semibold">
              <Eye className="w-4 h-4 mr-2" /> {previewing ? "Previewing…" : "Preview matches"}
            </Button>
            <Button variant="outline"
                    onClick={() => setOpen(true)}
                    data-testid="admin-purge-open-btn"
                    className="border-rose-300 text-rose-700 hover:bg-rose-100 font-semibold">
              <Trash2 className="w-4 h-4 mr-2" /> Purge demo data…
            </Button>
          </div>
        </div>
        {lastResult && (
          <div className="mt-4 p-3 border border-slate-200 bg-white rounded text-xs" data-testid="admin-purge-result">
            <div className="font-semibold text-slate-800 mb-2">
              {lastResult.dry_run
                ? `Preview — ${lastResult.total_matched} records would be removed`
                : `Last purge — ${lastResult.total_removed} records at ${new Date(lastResult.purged_at).toLocaleString()}`}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 font-mono">
              {Object.entries(lastResult.counts).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-slate-500">{k}</span>
                  <span className={v > 0 ? "text-rose-700 font-semibold" : "text-slate-400"}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirmText(""); }}>
        <DialogContent className="sm:max-w-md" data-testid="admin-purge-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" /> Confirm workspace purge
            </DialogTitle>
            <DialogDescription>
              This permanently deletes matching test/demo records across the workspace. Users,
              payments, and audit trails are preserved. Type <span className="font-mono font-semibold">PURGE</span> to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={confirmText}
                   onChange={(e) => setConfirmText(e.target.value)}
                   placeholder="Type PURGE to confirm"
                   data-testid="admin-purge-confirm-input"
                   autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="admin-purge-cancel-btn">Cancel</Button>
            <Button onClick={purge}
                    disabled={busy || confirmText !== "PURGE"}
                    data-testid="admin-purge-submit-btn"
                    className="bg-rose-600 hover:bg-rose-700 text-white font-semibold">
              {busy ? "Purging…" : "Purge now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
